// controller-worktree-safe-file-write.ts
//
// Phase 06f R1 (review beta.4 H3, spec E28c). The controller writes two kinds
// of file inside the mission worktree — `.avatar/PLAN.md` and
// `.avatar/tickets/*.json` — and the worker has Write/Bash in that same
// worktree between ticks. `fs.writeFile` follows a symlink at the final
// component and `mkdir -p`/`readdir` follow one at any parent, so a link left
// by a worker would turn a controller write into a write OUTSIDE the worktree.
//
// One path for both writers (DRY): every parent component must be a real
// directory (lstat, never stat), then tmp + `rename` via `writeTextAtomic` —
// `rename` REPLACES a symlink at the target instead of writing through it.
// No fsync: the durable point is the git commit + journal that follow (YAGNI).
// TOCTOU between lstat and rename is outside the threat model: the controller
// only writes here while no worker session runs (after verify / tick boundary).
import { promises as fs } from "node:fs";
import { basename, join, posix } from "node:path";
import { writeTextAtomic } from "../filesystem-helpers.js";

function refused(shown: string): Error {
  return new Error(`controller_write_refused: ${shown} không phải thư mục thật`);
}

async function assertRealDir(abs: string, shown: string): Promise<boolean> {
  try {
    if (!(await fs.lstat(abs)).isDirectory()) throw refused(shown);
    return true;
  } catch (error) {
    if ((error as NodeJS.ErrnoException).code === "ENOENT") return false;
    throw error;
  }
}

/**
 * Makes sure every component of `relDir` (repo-relative, `/`-separated, fixed
 * by the controller) under `repoDir` is a real directory, creating missing
 * ones one level at a time. A component that exists as a symlink or a file ⇒
 * throws `controller_write_refused: <component path> …` (fail closed).
 * Returns the absolute directory.
 */
export async function ensureRealControllerDir(repoDir: string, relDir: string): Promise<string> {
  const parts = relDir.split("/").filter((p) => p.length > 0 && p !== ".");
  if (parts.includes("..")) throw refused(relDir);
  // `repoDir` itself is the controller's own path (never a worker's); created
  // as `ensureDir` did before. Only the components BELOW it are checked.
  await fs.mkdir(repoDir, { recursive: true });
  let abs = repoDir;
  for (let i = 0; i < parts.length; i++) {
    abs = join(abs, parts[i] as string);
    const shown = parts.slice(0, i + 1).join("/");
    if (await assertRealDir(abs, shown)) continue;
    try {
      await fs.mkdir(abs);
    } catch (error) {
      if ((error as NodeJS.ErrnoException).code !== "EEXIST") throw error;
      if (!(await assertRealDir(abs, shown))) throw refused(shown);
    }
  }
  return abs;
}

/** Writes `content` at repo-relative `relPath` without following any symlink on the way. */
export async function writeControllerWorktreeFile(
  repoDir: string,
  relPath: string,
  content: string,
): Promise<void> {
  const dir = await ensureRealControllerDir(repoDir, posix.dirname(relPath));
  await writeTextAtomic(join(dir, basename(relPath)), content);
}
